import { z } from 'zod';

// ── Consent categories ────────────────────────────────────
// Internal canonical categories (Concord parity). NEVER rename the stored
// values, API category names, or event codes. The `marketing` category is
// labeled "Marketing (Advertising)" in UI only — the stored value stays
// `marketing`.
export const CONSENT_CATEGORIES = [
  'analytics',
  'functional',
  'marketing',
] as const;
export type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];

// Per-category permission booleans. NULL is expressed at the source layer
// (the consent row) as "this source never spoke"; the resolved snapshot is
// always fully populated.
export type ConsentCategories = Record<ConsentCategory, boolean>;

// ── Organization consent behavior ─────────────────────────
// Default state of each category before any consent source speaks.
export const CONSENT_BEHAVIORS = [
  'notRequired',
  'implied',
  'express',
  'custom',
] as const;
export type ConsentBehavior = (typeof CONSENT_BEHAVIORS)[number];

// Per-category map read only when behavior = 'custom'. 'implied' = allowed by
// default (until denied); 'express' = denied until granted — the same posture
// names as the top-level CONSENT_BEHAVIORS.
export const CUSTOM_CATEGORY_STATES = ['implied', 'express'] as const;
export type CustomCategoryState = (typeof CUSTOM_CATEGORY_STATES)[number];
export type CustomCategoryMap = Record<ConsentCategory, CustomCategoryState>;

// What happens to an event whose analytics permission is denied at capture.
export const DENIED_EVENT_HANDLINGS = ['drop', 'anonymize'] as const;
export type DeniedEventHandling = (typeof DENIED_EVENT_HANDLINGS)[number];

// Whether SERVER-SIDE (secret-key) events bypass the analytics-denied gate.
// Client-side (public-key / browser SDK) events ALWAYS go through the gate.
//   gate          → no exemption; server events are gated like client events
//   exemptRevenue → exempt server-side revenue (events carrying `value`) only
//   exempt        → exempt all server-side events
// Default is `exemptRevenue`: a server-recorded transaction has a separate
// lawful basis, but a generic server event still respects analytics consent.
export const SERVER_SIDE_EVENT_HANDLINGS = [
  'gate',
  'exemptRevenue',
  'exempt',
] as const;
export type ServerSideEventHandling =
  (typeof SERVER_SIDE_EVENT_HANDLINGS)[number];

/**
 * Whether an event is exempt from the analytics-denied ingest gate under the
 * resolved `serverSideEventHandling` policy. Only SERVER-SIDE events (secret-key
 * ingest) can be exempt; `exemptRevenue` further requires a monetary `value`.
 */
export const serverSideEventExempt = (
  mode: ServerSideEventHandling,
  isServerSide: boolean,
  isRevenue: boolean,
): boolean =>
  isServerSide &&
  (mode === 'exempt' || (mode === 'exemptRevenue' && isRevenue));

// ── Consent source priority ladder ────────────────────────
// Exactly one source is authoritative at any time. Highest priority wins.
// Order matters: index 0 is the highest-priority source.
export const CONSENT_SOURCES = [
  'logspot_api', // explicit Consent API / setConsent()
  'concord', // preferred CMP integration
  'onetrust', // third-party CMP
  'usercentrics', // third-party CMP
  'cookiebot', // third-party CMP
  'gcm', // Google Consent Mode reader (read-only)
  'admin_default', // org consentBehavior resolution
] as const;
export type ConsentSource = (typeof CONSENT_SOURCES)[number];

// Sources whose category outcome is TRUSTED over browser privacy signals.
// Used to suppress the no-CMP GPC/GPP fallback: when any of these produced the
// authoritative row, signals are recorded but never override the source's
// category outcome. `admin_default` is NOT a CMP — it is the absence of a real
// source. `gcm` is deliberately EXCLUDED: Google Consent Mode state can be a
// site-configured `default` (no banner, no human choice — the reader cannot
// tell a default from a real update), so a user's explicit GPC signal must be
// allowed to override a GCM "granted".
export const CMP_SOURCES: ReadonlySet<ConsentSource> = new Set<ConsentSource>([
  'logspot_api',
  'concord',
  'onetrust',
  'usercentrics',
  'cookiebot',
]);

// Sources that count as EXPLICIT human consent (a person actually answered —
// via our API or an interactive CMP). Excludes `admin_default` (org posture,
// nobody answered), `gpc_fallback` (a signal-forced restriction), and `gcm`
// (often a site-configured default; its "granted" is ordinary permission, never
// proof of a human yes). Consumed by gates that need a real lawful basis, e.g.
// international company visitor identification.
export const EXPLICIT_CONSENT_SOURCES: ReadonlySet<ConsentSource> =
  new Set<ConsentSource>([
    'logspot_api',
    'concord',
    'onetrust',
    'usercentrics',
    'cookiebot',
  ]);

/** True when `source` counts as explicit human consent (see the set above). */
export const isExplicitConsentSource = (source: string): boolean =>
  EXPLICIT_CONSENT_SOURCES.has(source as ConsentSource);

// Priority TIER rank — lower number wins. Per the locked ladder
// `logspot_api > concord > (onetrust | usercentrics | cookiebot) >
// gcm > admin_default`, the three third-party CMPs share one tier (rank
// 2): an equal-tier writer overwrites (latest-write-wins within a tier).
const SOURCE_PRIORITY: Record<ConsentSource, number> = {
  logspot_api: 0,
  concord: 1,
  onetrust: 2,
  usercentrics: 2,
  cookiebot: 2,
  gcm: 3,
  admin_default: 4,
};

const LOWEST_PRIORITY = 99;

/**
 * Priority rank of a consent source — lower is higher priority. Unknown
 * sources rank below every known source (treated as lowest priority) so an
 * unrecognized writer can never clobber a known authoritative row.
 */
export const consentSourcePriority = (source: string): number =>
  SOURCE_PRIORITY[source as ConsentSource] ?? LOWEST_PRIORITY;

/**
 * True when `incoming` is allowed to overwrite a row currently held by
 * `current`. Equal priority is allowed to overwrite (latest write wins within
 * the same source tier). A strictly lower-priority incoming source loses.
 */
export const sourceCanOverwrite = (
  incoming: string,
  current: string,
): boolean => consentSourcePriority(incoming) <= consentSourcePriority(current);

// ── Privacy signals ───────────────────────────────────────
// Rights/signals present at capture, stored independently from consent.
export type PrivacySignals = {
  gpc?: boolean;
  doNotSellShare?: boolean;
  gppDetected?: boolean;
  gppString?: string | null;
  gppSections?: unknown[] | null;
};

// ── Canonical letter encodings ────────────────────────────
// privacy_categories alphabet: a (analytics) < f (functional) < m (marketing).
export const CATEGORY_TO_LETTER: Record<ConsentCategory, string> = {
  analytics: 'a',
  functional: 'f',
  marketing: 'm',
};
export const LETTER_TO_CATEGORY: Record<string, ConsentCategory> = {
  a: 'analytics',
  f: 'functional',
  m: 'marketing',
};
// Canonical order for category letters.
export const CATEGORY_LETTER_ORDER = ['a', 'f', 'm'] as const;

// privacy_signals alphabet: d (doNotSellShare) < g (GPC) < p (GPP detected).
export const SIGNAL_LETTER_ORDER = ['d', 'g', 'p'] as const;
export type SignalLetter = (typeof SIGNAL_LETTER_ORDER)[number];

// ── Wire DTO (shared by /consent + track/identify aliases) ─
// Partial category updates allowed; signals optional. Server re-canonicalizes
// any privacy_categories/privacy_signals strings rather than trusting client
// order.
export const consentCategoryBoolSchema = z
  .object({
    analytics: z.boolean().optional(),
    functional: z.boolean().optional(),
    marketing: z.boolean().optional(),
  })
  .strict();

export const privacySignalsSchema = z
  .object({
    gpc: z.boolean().optional(),
    doNotSellShare: z.boolean().optional(),
    gppDetected: z.boolean().optional(),
    gppString: z.string().nullable().optional(),
    gppSections: z.array(z.unknown()).nullable().optional(),
  })
  .strict();
